import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  CallSplit as CallSplitIcon,
  School as SchoolIcon,
  GradeOutlined as GradeIcon,
  LocationOn as LocationIcon,
  Group as TeamIcon,
  PlayArrow as ProcessIcon,
  CloudQueue as BulkIcon,
  SingleBed as SingleIcon
} from '@mui/icons-material';
// import { useAuth } from '../../../../../auth/AuthContext';
// import { useSnackbar } from 'notistack';

// Import step components (we'll create these)
import ParentSchoolSelection from './split_workflow/ParentSchoolSelection';
import GradeAssignment from './split_workflow/GradeAssignment';
import AddressManagement from './split_workflow/AddressManagement';
import TeamAssignment from './split_workflow/TeamAssignment';
import ProcessingConfirmation from './split_workflow/ProcessingConfirmation';
import BulkProcessingWorkflow from './BulkProcessingWorkflow';

const SplitSchoolWorkflow = () => {
  // const { user } = useAuth();
  // const { enqueueSnackbar } = useSnackbar();
  
  // Processing mode state
  const [processingMode, setProcessingMode] = useState('individual'); // 'individual' or 'bulk'
  
  // Workflow state
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [workflowData, setWorkflowData] = useState({
    parentSchool: null,
    parentNcessch: '',
    splits: [],
    teamAssignments: {},
    validationResults: null
  });

  const steps = [
    {
      label: 'Select Parent School',
      description: 'Choose the school to split',
      icon: <SchoolIcon />,
      component: ParentSchoolSelection
    },
    {
      label: 'Assign Grades',
      description: 'Define grade ranges for each split',
      icon: <GradeIcon />,
      component: GradeAssignment
    },
    {
      label: 'Set Addresses',
      description: 'Assign addresses to split schools',
      icon: <LocationIcon />,
      component: AddressManagement
    },
    {
      label: 'Assign Teams',
      description: 'Configure team assignments',
      icon: <TeamIcon />,
      component: TeamAssignment
    },
    {
      label: 'Process Split',
      description: 'Review and execute the split',
      icon: <ProcessIcon />,
      component: ProcessingConfirmation
    }
  ];

  // Workflow navigation
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setWorkflowData({
      parentSchool: null,
      parentNcessch: '',
      splits: [],
      teamAssignments: {},
      validationResults: null
    });
    setIsProcessing(false);
  };

  // Data update handlers
  const updateWorkflowData = (stepData) => {
    setWorkflowData(prev => ({
      ...prev,
      ...stepData
    }));
  };

  // Validation for each step
  const isStepValid = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Parent school selection
        return workflowData.parentSchool && workflowData.parentNcessch;
      case 1: // Grade assignment
        return workflowData.splits && workflowData.splits.length > 0;
      case 2: // Address management
        return workflowData.splits.every(split => 
          split.address && 
          split.address.street && 
          split.address.city && 
          split.address.state && 
          split.address.zip
        );
      case 3: // Team assignment
        return true; // Team assignment is optional
      case 4: // Processing confirmation
        return workflowData.validationResults && workflowData.validationResults.isValid;
      default:
        return false;
    }
  };

  // Get current step component
  const getCurrentStepComponent = () => {
    const StepComponent = steps[activeStep].component;
    return (
      <StepComponent
        workflowData={workflowData}
        onDataUpdate={updateWorkflowData}
        onNext={handleNext}
        onBack={handleBack}
        isValid={isStepValid(activeStep)}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
      />
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CallSplitIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Split School Processing
            </Typography>
            {isProcessing && (
              <Chip 
                label="Processing..." 
                color="primary" 
                icon={<CircularProgress size={16} />}
              />
            )}
          </Box>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Split an existing school into multiple grade-level components with separate addresses and processing.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Split School Process</AlertTitle>
            This workflow will create new school records with unique UUIDs while maintaining the same NCES ID. 
            Each split school will have its own address, enrollment data, and can be assigned to teams independently.
          </Alert>
        </CardContent>
      </Card>

      {/* Processing Mode Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={processingMode}
          onChange={(e, newMode) => setProcessingMode(newMode)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<SingleIcon />}
            label="Individual Split"
            value="individual"
            iconPosition="start"
            sx={{ 
              minHeight: 64,
              textTransform: 'none',
              '&.Mui-selected': {
                color: 'primary.main'
              }
            }}
          />
          <Tab
            icon={<BulkIcon />}
            label="Bulk Processing"
            value="bulk"
            iconPosition="start"
            sx={{ 
              minHeight: 64,
              textTransform: 'none',
              '&.Mui-selected': {
                color: 'info.main'
              }
            }}
          />
        </Tabs>
      </Paper>

      {/* Conditional Content Based on Processing Mode */}
      {processingMode === 'bulk' ? (
        <BulkProcessingWorkflow />
      ) : (
        <>
          {/* Progress Stepper */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((step, index) => (
                  <Step key={step.label} completed={index < activeStep}>
                    <StepLabel
                      StepIconComponent={({ active, completed }) => (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'grey.300',
                            color: completed || active ? 'white' : 'grey.600'
                          }}
                        >
                          {step.icon}
                        </Box>
                      )}
                    >
                      <Typography variant="subtitle2">{step.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.description}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          {/* Step Content */}
          <Card>
            <CardContent>
              {getCurrentStepComponent()}
            </CardContent>
          </Card>

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0 || isProcessing}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            
            {activeStep === steps.length - 1 ? (
              <Button onClick={handleReset} disabled={isProcessing}>
                Start Over
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid(activeStep) || isProcessing}
              >
                Next
              </Button>
            )}
          </Box>

          {/* Workflow Summary (when data exists) */}
          {(workflowData.parentSchool || workflowData.splits.length > 0) && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Workflow Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {workflowData.parentSchool && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="primary">
                        Parent School:
                      </Typography>
                      <Typography variant="body2">
                        {workflowData.parentSchool.name} ({workflowData.parentNcessch})
                      </Typography>
                    </Grid>
                  )}
                  
                  {workflowData.splits.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="primary">
                        Split Schools:
                      </Typography>
                      {workflowData.splits.map((split, index) => (
                        <Typography key={index} variant="body2">
                          {split.displayName} - Grades: {split.gradesServed?.join(', ')}
                        </Typography>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default SplitSchoolWorkflow;
